/**
 * @fileoverview BankBalanceModal - Modal for managing bank account balances.
 *
 * Allows users to:
 * - View and edit bank account balances for the selected month
 * - Add new bank accounts (created when saved)
 * - Delete bank accounts for this month only
 * - See total bank balance
 */
import { useState, useEffect, useRef } from 'react';
import ToggleSwitch from '../shared/ToggleSwitch';
import ButtonSpinner from '../Loading/ButtonSpinner';
import { calculateExpression } from '../../utils/calculateExpression';
import { formatMoney } from '../../utils/moneyUtils';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import styles from './BankBalanceModal.module.css';

/**
 * BankBalanceModal Component
 *
 * Modal for managing monthly bank account balances.
 * Simplified: accounts are created/managed directly within each month.
 *
 * Props:
 * - isOpen (boolean): Whether modal is visible
 * - onClose (function): Called when modal should close
 * - balances (array): Current balances from useBankBalances hook
 * - onDeleteBalance (function): Delete balance entry for current month
 * - onSaveBalances (function): Save updated balances
 * - totalBalance (number): Total of all balances
 * - onBalanceChange (function): Update local balance (for real-time total)
 * - readOnly (boolean): Whether modal is in read-only mode (ceased budget)
 */
function BankBalanceModal({
    isOpen,
    onClose,
    balances,
    onDeleteBalance,
    onSaveBalances,
    totalBalance,
    onBalanceChange,
    readOnly = false
}) {
    const [newAccountName, setNewAccountName] = useState('');
    const [newAccountBalance, setNewAccountBalance] = useState('');
    const [newAccountTreatAsNegative, setNewAccountTreatAsNegative] = useState(false);
    const [showAddAccount, setShowAddAccount] = useState(false);
    const [sortedBalances, setSortedBalances] = useState([]);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [focusedField, setFocusedField] = useState(null); // Track which field is focused
    const [fieldErrors, setFieldErrors] = useState({}); // Track per-field errors
    const inputRefs = useRef({}); // Store refs for each input field

    // Lock body scroll when modal is open (prevents background scrolling on mobile)
    useModalScrollLock(isOpen);

    // Reset saving state when modal opens
    useEffect(() => {
        if (isOpen) {
            setSaving(false);
        }
    }, [isOpen]);

    // Initialize sorted balances when modal opens
    useEffect(() => {
        if (isOpen && balances && balances.length > 0) {
            // Balances are already sorted by sortOrder from backend
            const sorted = [...balances].map((bal, index) => {
                const isZero = parseFloat(bal.balance) === 0;
                return {
                    accountName: bal.accountName,
                    balance: bal.balance.toFixed(2),
                    equation: bal.equation || '', // Store equation (empty if zero)
                    displayValue: isZero ? '' : formatMoney(bal.balance), // Show empty for zero, formatted with commas otherwise
                    sortOrder: bal.sortOrder || (index + 1),
                    treatAsNegative: bal.treatAsNegative || false,
                    fromPreviousMonth: bal.fromPreviousMonth
                };
            });
            setSortedBalances(sorted);
            // Clear any field errors when modal opens
            setFieldErrors({});
        } else if (isOpen) {
            setSortedBalances([]);
            setFieldErrors({});
        }
    }, [isOpen]); // Only run when modal opens, not when balances change

    if (!isOpen) return null;

    // Helper function to check if a string is a literal zero (0, 0.0, 0.00, etc.)
    const isLiteralZero = (str) => {
        if (!str || str.trim() === '') return true;
        // Remove all leading zeros, decimal points, and trailing zeros
        const normalized = str.trim().replace(/^0+/, '').replace(/\.0+$/, '').replace(/^\./, '');
        return normalized === '' || normalized === '0';
    };

    const handleBalanceFocus = (accountName) => {
        setFocusedField(accountName);
        // When focusing, show the equation if it exists, otherwise show empty string (not "0")
        setSortedBalances(prev =>
            prev.map(bal =>
                bal.accountName === accountName
                    ? { ...bal, displayValue: bal.equation || '' }
                    : bal
            )
        );

        // Move cursor to end and scroll to show it
        // Use setTimeout to ensure the displayValue is updated first
        setTimeout(() => {
            const input = inputRefs.current[accountName];
            if (input) {
                const length = input.value.length;
                // Set cursor position to end
                input.setSelectionRange(length, length);
                // Scroll to end of input (shows the cursor)
                input.scrollLeft = input.scrollWidth;
            }
        }, 0);
    };

    const handleBalanceChange = (accountName, value) => {
        // Clear any error for this field
        setFieldErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[accountName];
            return newErrors;
        });

        // Update display value while typing (don't update equation yet)
        setSortedBalances(prev =>
            prev.map(bal =>
                bal.accountName === accountName
                    ? { ...bal, displayValue: value }
                    : bal
            )
        );
    };

    const handleBalanceBlur = (accountName, value) => {
        setFocusedField(null);

        // If empty, treat as zero
        if (!value || value.trim() === '') {
            setSortedBalances(prev =>
                prev.map(bal =>
                    bal.accountName === accountName
                        ? {
                            ...bal,
                            balance: '0.00',
                            equation: '', // Empty equation for zero
                            displayValue: '' // Show empty when not focused
                        }
                        : bal
                )
            );
            // Update real-time total in parent
            onBalanceChange(accountName, '0.00');
            return;
        }

        // Try to calculate the expression
        const result = calculateExpression(value);

        if (!result.isValid) {
            // Show error for this field
            setFieldErrors(prev => ({
                ...prev,
                [accountName]: result.error
            }));
            // Store the invalid input so user can fix it on next focus
            setSortedBalances(prev =>
                prev.map(bal =>
                    bal.accountName === accountName
                        ? {
                            ...bal,
                            displayValue: value, // Keep showing the invalid input
                            equation: value // Update equation to the invalid input so it shows on focus
                        }
                        : bal
                )
            );
            return;
        }

        // Valid calculation - update with formatted result
        const formattedValue = result.result.toFixed(2);
        const isResultZero = parseFloat(formattedValue) === 0;

        // Only save equation if result is zero AND it's NOT a literal zero string
        // This preserves formulas like "5-5" but not literal "0" or "0.00"
        const shouldSaveEquation = isResultZero ? !isLiteralZero(value) : true;

        // Format display value with commas (but not for zero)
        const displayValue = isResultZero ? '' : formatMoney(result.result);

        setSortedBalances(prev =>
            prev.map(bal =>
                bal.accountName === accountName
                    ? {
                        ...bal,
                        balance: formattedValue,
                        equation: shouldSaveEquation ? value : '', // Save equation unless it's literal zero
                        displayValue: displayValue // Show empty for zero result, otherwise show formatted with commas
                    }
                    : bal
            )
        );

        // Update real-time total in parent
        onBalanceChange(accountName, formattedValue);
    };

    const handleAddAccount = () => {
        if (!newAccountName.trim()) {
            setError('Account name is required');
            return;
        }

        const accountName = newAccountName.trim();

        // Check for duplicate account name
        if (sortedBalances.some(bal => bal.accountName === accountName)) {
            setError('An account with this name already exists for this month');
            return;
        }

        // Validate and calculate the balance (treat empty as zero)
        let formattedBalance;

        if (!newAccountBalance || newAccountBalance.trim() === '') {
            // Empty balance defaults to 0
            formattedBalance = '0.00';
        } else {
            const result = calculateExpression(newAccountBalance);
            if (!result.isValid) {
                setError(`Invalid balance: ${result.error}`);
                return;
            }
            formattedBalance = result.result.toFixed(2);
        }

        // Add to end of sorted balances array
        setSortedBalances(prev => [
            ...prev,
            {
                accountName,
                balance: formattedBalance,
                equation: newAccountBalance, // Store what user entered
                displayValue: formattedBalance,
                treatAsNegative: newAccountTreatAsNegative,
                sortOrder: prev.length + 1,
                fromPreviousMonth: false
            }
        ]);

        // Note: We don't call onBalanceChange here because the new account
        // will be saved when the user clicks "Save Balances"

        setNewAccountName('');
        setNewAccountBalance('');
        setNewAccountTreatAsNegative(false);
        setShowAddAccount(false);
        setError('');
    };

    const handleDeleteBalance = async (accountName) => {
        if (window.confirm(`Delete "${accountName}"? This will remove it from this month only.`)) {
            const result = await onDeleteBalance(accountName);
            if (result.success) {
                // Remove from sorted balances array
                setSortedBalances(prev =>
                    prev.filter(bal => bal.accountName !== accountName)
                );
                // The deleteBalance function already calls fetchBalances() which updates the parent
                // No need to call onBalanceChange here
            } else {
                setError(result.error || 'Failed to delete balance');
            }
        }
    };

    const handleSave = async () => {
        // Check if there are any field errors
        if (Object.keys(fieldErrors).length > 0) {
            setError('Please fix all errors before saving');
            return;
        }

        setSaving(true);
        setError('');

        // Convert sorted balances to array format
        // The order in the array determines the sort order (backend assigns 1, 2, 3...)
        const balancesToSave = sortedBalances.map(bal => {
            const balance = parseFloat(bal.balance) || 0;
            const isResultZero = balance === 0;

            // Only save equation if:
            // 1. Balance is not zero, OR
            // 2. Balance is zero but equation is not a literal zero (e.g., "5-5" should be saved)
            let equationToSave = bal.equation || '';
            if (isResultZero && isLiteralZero(equationToSave)) {
                equationToSave = '';
            }

            return {
                accountName: bal.accountName,
                balance: balance,
                equation: equationToSave,
                treatAsNegative: bal.treatAsNegative || false
            };
        });

        const result = await onSaveBalances(balancesToSave);
        setSaving(false);

        if (result.success) {
            onClose();
        } else {
            setError(result.error || 'Failed to save balances');
        }
    };

    // Drag and drop handlers (similar to SortCategoriesModal)
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newBalances = [...sortedBalances];
        const draggedItem = newBalances[draggedIndex];

        // Remove from old position
        newBalances.splice(draggedIndex, 1);
        // Insert at new position
        newBalances.splice(index, 0, draggedItem);

        setSortedBalances(newBalances);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <button
                    onClick={onClose}
                    disabled={saving}
                    className={styles.closeButton}
                    aria-label="Close"
                >
                    ×
                </button>

                <h2 className={styles.title}>Update Bank Balances</h2>

                {error && (
                    <div className={styles.error}>
                        {error}
                    </div>
                )}

                {/* Bank Accounts List */}
                <div className={styles.accountsList}>
                    {sortedBalances.length === 0 ? (
                        <p className={styles.emptyMessage}>No bank accounts yet. Add one below!</p>
                    ) : (
                        sortedBalances.map((bal, index) => (
                            <div
                                key={bal.accountName}
                                className={`${styles.accountRow} ${draggedIndex === index ? styles.dragging : ''}`}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDragEnd={handleDragEnd}
                            >
                                <div className={styles.dragHandle}>⋮⋮</div>
                                <div className={styles.accountInfo}>
                                    <div className={styles.accountName}>
                                        {bal.accountName}
                                        {bal.fromPreviousMonth && (
                                            <span className={styles.badge}>From Previous Month</span>
                                        )}
                                    </div>
                                    <div className={styles.balanceInputWrapper}>
                                        {bal.treatAsNegative && <span className={styles.negativeSign}>−</span>}
                                        <div style={{ flex: 1 }}>
                                            <input
                                                ref={(el) => inputRefs.current[bal.accountName] = el}
                                                type="text"
                                                value={bal.displayValue}
                                                onFocus={() => handleBalanceFocus(bal.accountName)}
                                                onChange={(e) => handleBalanceChange(bal.accountName, e.target.value)}
                                                onBlur={(e) => handleBalanceBlur(bal.accountName, e.target.value)}
                                                className={`${styles.balanceInput} ${fieldErrors[bal.accountName] ? styles.inputError : ''}`}
                                                placeholder="0.00"
                                                disabled={readOnly}
                                            />
                                            {fieldErrors[bal.accountName] && (
                                                <div className={styles.fieldError}>
                                                    {fieldErrors[bal.accountName]}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {!readOnly && (
                                    <button
                                        onClick={() => handleDeleteBalance(bal.accountName)}
                                        className={styles.deleteButton}
                                        title="Delete balance for this month"
                                        aria-label="Delete balance"
                                    >
                                        <span className={styles.deleteButtonText}>Delete</span>
                                        <span className={styles.deleteButtonIcon}>×</span>
                                    </button>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Total Balance Display */}
                <div className={styles.totalSection}>
                    <strong>Total Bank Balance:</strong>
                    <span className={styles.totalAmount}>
                        ${formatMoney(totalBalance)}
                    </span>
                </div>

                {/* Add Account Section */}
                {!readOnly && (
                    <div className={styles.addAccountSection}>
                        {!showAddAccount ? (
                            <button
                                onClick={() => setShowAddAccount(true)}
                                className={styles.addButton}
                            >
                                + Add Bank Account
                            </button>
                    ) : (
                        <div className={styles.addAccountForm}>
                            <div className={styles.addAccountInputs}>
                                <div className={styles.inputGroup}>
                                    <label className={styles.inputLabel}>Account Name</label>
                                    <input
                                        type="text"
                                        value={newAccountName}
                                        onChange={(e) => setNewAccountName(e.target.value)}
                                        placeholder="e.g., Chase Checking"
                                        className={styles.accountNameInput}
                                        maxLength={30}
                                        autoFocus
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label className={styles.inputLabel}>Balance</label>
                                    <input
                                        type="text"
                                        value={newAccountBalance}
                                        onChange={(e) => setNewAccountBalance(e.target.value)}
                                        placeholder="0.00 or 1000-250"
                                        className={styles.accountBalanceInput}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <ToggleSwitch
                                        checked={newAccountTreatAsNegative}
                                        onChange={(checked) => setNewAccountTreatAsNegative(checked)}
                                        label="Negative Balance"
                                    />
                                </div>
                            </div>
                            <div className={styles.addAccountButtons}>
                                <button
                                    onClick={() => {
                                        setShowAddAccount(false);
                                        setNewAccountName('');
                                        setNewAccountBalance('');
                                        setNewAccountTreatAsNegative(false);
                                        setError('');
                                    }}
                                    className={styles.cancelAccountButton}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddAccount}
                                    className={styles.saveAccountButton}
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    )}
                    </div>
                )}

                {/* Modal Footer */}
                <div className={styles.footer}>
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className={readOnly ? styles.saveButton : styles.cancelButton}
                    >
                        {readOnly ? 'Close' : 'Cancel'}
                    </button>
                    {!readOnly && (
                        <button
                            onClick={handleSave}
                            className={styles.saveButton}
                            disabled={saving}
                        >
                            {saving && <ButtonSpinner />}
                            {saving ? 'Saving...' : 'Save Balances'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default BankBalanceModal;
